import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { IUseOPopperProps, IPopperAction } from './OPopper.types';

export const useOPopper = ({
  isOpen = false,
  isActive,
  onOpen,
  onClose,
  offset,
  withArrow,
  isDismissible = true,
  closeOnScroll = true,
}: IUseOPopperProps) => {
  // State
  const [open, setOpen] = useState<boolean>(isOpen);
  const [active, setActive] = useState(isActive);
  const [arrowRef, setArrowRef] = useState<HTMLElement | null>(null);
  const [submenuAnchor, setSubmenuAnchor] = useState<HTMLElement | null>(null);
  const [submenuActions, setSubmenuActions] = useState<IPopperAction[] | null>(null);

  // Refs
  const anchorRef = useRef<HTMLButtonElement>(null);
  const popperRef = useRef<HTMLDivElement>(null);
  const subMenuPopperRef = useRef<HTMLDivElement>(null);

  const modifiers = useMemo(
    () => [
      {
        name: 'offset',
        options: { offset: offset ?? [0, 0] },
      },
      {
        name: 'arrow',
        enabled: withArrow,
        options: { element: arrowRef },
      },
    ],
    [offset, withArrow, arrowRef]
  );

  // Handlers
  const toggleClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      setOpen((prev) => {
        const next = !prev;
        if (next) {
          onOpen?.();
          setActive(true);
        } else {
          onClose?.();
          setActive(false);
        }
        return next;
      });
    },
    [onOpen, onClose]
  );

  const handleClose = useCallback(() => {
    setOpen(false);
    setActive(false);
    onClose?.();
    setSubmenuAnchor(null);
    setSubmenuActions(null);
  }, [onClose]);

  const handleSubmenuToggle = useCallback(
    (action: IPopperAction, eventTarget: HTMLElement) => {
      if (submenuAnchor && submenuActions === action.submenu) {
        // close if already open
        setSubmenuAnchor(null);
        setSubmenuActions(null);
      } else {
        setSubmenuAnchor(eventTarget);
        setSubmenuActions(action.submenu ?? null);
      }
    },
    [submenuActions, submenuAnchor]
  );

  const handleSubmenuClick = useCallback(
    (action: IPopperAction) => {
      action.onClick?.();
      handleClose();
    },
    [handleClose]
  );

  // Ref callback for arrow element
  const arrowRefCallback = useCallback((element: HTMLElement | null) => {
    setArrowRef(element);
  }, []);

  // ----------------- Effects -----------------
  // Close on outside click
  useEffect(() => {
    const handleOutsidePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (
        popperRef.current &&
        !popperRef.current.contains(target) &&
        !anchorRef.current?.contains(target) &&
        !subMenuPopperRef.current?.contains(target) &&
        isDismissible
      ) {
        handleClose();
      }
    };

    // Use the document where the anchor element is attached (handles PiP window case)
    const doc = anchorRef.current?.ownerDocument || document;
    doc.addEventListener('pointerdown', handleOutsidePointerDown);
    return () => {
      doc.removeEventListener('pointerdown', handleOutsidePointerDown);
    };
  }, [handleClose, isDismissible, submenuAnchor]);

  // Close on ESC
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isDismissible) handleClose();
    };

    const doc = anchorRef.current?.ownerDocument || document;
    doc.addEventListener('keydown', handleKey);
    return () => doc.removeEventListener('keydown', handleKey);
  }, [handleClose, isDismissible]);

  // Sync external isOpen
  useEffect(() => {
    if (isOpen !== undefined) {
      setOpen(isOpen);
    }
  }, [isOpen]);

  // Close when any scroll happens anywhere
  useEffect(() => {
    if (!closeOnScroll) return;

    const handleScroll = () => {
      if (open) {
        handleClose();
      }
    };

    const win = anchorRef.current?.ownerDocument?.defaultView || window;
    win.addEventListener('scroll', handleScroll, true);

    return () => {
      win.removeEventListener('scroll', handleScroll, true);
    };
  }, [handleClose, open, closeOnScroll]);

  return {
    // State
    open,
    active,
    arrowRef,
    modifiers,
    submenuAnchor,
    submenuActions,
    // Refs
    anchorRef,
    popperRef,
    subMenuPopperRef,
    // Handlers
    toggleClick,
    handleClose,
    handleSubmenuClick,
    handleSubmenuToggle,
    // Setters
    arrowRefCallback,
    setSubmenuAnchor,
    setSubmenuActions,
  };
};
